import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Workflow } from '@app/models';
import WorkflowStepEventArgs from '@app/models-internal/workflow-step-event-args';
import Reason from '@app/models/reason';
import WorkflowDisplayNodeViewModel from '@app/models/workflow-display-node-view-model';
import WorkflowStep from '@app/models/workflow-step';
import { Subject } from 'rxjs';

import { LogService, ValueService } from '@oa/shared-core';

/**
 * Workflow Service provides generic workflow functionality.
 * A workflow step completed subject (similar to the booking service).
 *
 * Only a step completed subject currently exists on this service.
 */
@Injectable()
export class WorkflowService {


  private readonly _serviceName = 'WorkflowService';

  private _stepCompleted: Subject<string>;

  // @Output() workflowStepCompleted = new EventEmitter<WorkflowStepEventArgs>();
  public workflowStepCompleted = new Subject<WorkflowStepEventArgs>();

  private _workflows: Workflow[] = new Array();

  constructor(private log: LogService, private http: HttpClient, private valueService: ValueService) {
    this._stepCompleted = new Subject<string>();
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  public allStepsComplete(steps: WorkflowStep[]) {
    if (!steps || steps.length === 0){
      return null;
    }

    if (steps.some(x=> !x.isComplete)){
      return false;
    }
    else{
      return true;
    }
  }


  /**
   * Gets an event that will fire when a step in the booking process has completed.
   * This event implies that the subscribers know the current step.
   * Example: once a user has succesfully paid for the reservation
   * @param workflowName
   */

  // JT - this doesn't make any sense since the workflowName is not used
  // can't monitor workflow status without knowing a specific workflow
  // turns out this is only used by a few components when workflowName = 'user-account-background-check'
  // throw exception if used with any other workflowName until this can be reworked
  getStepCompleted(workflowName: string) {
    if (workflowName !== 'user-account-background-check') {
      throw new Error('getStepCompleted only works with workflowName = user-account-background-check');
    }
    return this._stepCompleted;
  }

  /**
   * Raises an event for a specified step name that will fire when a step in the workflow has completed.
   * Note: this event DOES NOT set the step's isComplete property to true.  This only raises the event on the service.
   * It is up to subscribers to respond to the event and do things like setting the isComplete property to true.
   * Example: once a user has succesfully paid for the reservation
   * @param workflowName
   */
  stepIsReadyToBeMarkedCompleted(workflowName: string, stepName: string, optionaldata?: any) {
    if (this.verboseLogging) {
      console.log(`${this._serviceName}.stepCompleted workflowName=${workflowName},stepName=${stepName}`);
    }

    const workflow = this.findWorkflow(workflowName);

    const step = workflow.steps.find((x) => x.step === stepName);

    const args = {
      step: step,
      workflow: workflow,
      data: optionaldata,
    } as WorkflowStepEventArgs;

    this.workflowStepCompleted.next(args);
  }

  /**
   * Sets a workflow Step to a completed status
   * @param workflowName
   * @param stepName
   * @param v
   */
  public setStepCompleted(workflowName: string, stepName: string, v?: boolean) {
    if (this.verboseLogging) {
      console.log(`${this._serviceName}.stepCompleted stepName=${stepName}, v=`, v);
    }

    if (v === undefined) {
      v = true;
    }

    let workflow = this.findWorkflow(workflowName);

    let step = workflow.steps.find((x) => x.step === stepName);

    if (!step){
      console.warn(`${this._serviceName}.setStepCompleted - no workflow step named ${stepName} in workflow: ${workflowName}`); 
      return;
    }

    step.isComplete = v;

    if (this.verboseLogging) {
      console.log(`${this._serviceName}.setStepCompleted workflowName=${workflowName},stepName=${stepName},isComplete=${v}`);
    }
  }




  /**
   * Sets a workflow step to completed by the workflow code (see setStepCompleted to use the workflow name instead)
   * @param code 
   * @param stepName 
   * @param v 
   */
  public setStepCompletedByWorkflowCode(code: string, stepName: string, v?: boolean){

    const workflow = this.getWorkflow(code);

    this.setStepCompleted(workflow.name,stepName,true);    
  }

  /**
   * Adds a new workflow to the managed list of workflows.P
   * Workflow name must be unique or the replaceIfExists needs to be true
   * in order to replace an existing workflow.
   * @param workflow
   */
  public addWorkflow(workflow: Workflow, replaceIfExists: boolean) {
    if (!workflow) {
      throw new Error('Parameter "workflow" required, but is null.');
    }
 
    let existingWorkflow = this._workflows.find((w) => w.name === workflow.name);

    if (existingWorkflow && !replaceIfExists) {
      throw new Error(`Workflow '${workflow.name}' already exists.`);
    }

    if (existingWorkflow && replaceIfExists) {
      this.removeWorkflow(existingWorkflow.name);
    }

    this._workflows.push(workflow);

    if (this.verboseLogging) {
      console.log(`WorkflowService.addWorkflow: Workflow '${workflow.name}'`);
    }

    this.printWorkflows();
  }

  /**
   * Removes a workflow from the list of managed workflows
   * @param workflowName
   */
  removeWorkflow(workflowName: string) {
    let workflow = this.findWorkflow(workflowName);

    this._workflows = this._workflows.filter((e) => e.name !== workflow.name);

    this.printWorkflows();
  }

  public printWorkflows() {
    if (this.verboseLogging) {
      console.log('WorkflowService.Workflows: ', JSON.stringify(this._workflows, null, 4));
    }
  }

  /**
   * Finds the workflow by name
   * @param workflowName
   */
  private findWorkflow(workflowName: string) {
    if (!workflowName) {
      throw new Error('Parameter "workflowName" required, but is null.');
    }

    let workflow = this._workflows.find((w) => w.name === workflowName);

    if (!workflow) {
      let candidateWorkflow = this._workflows.find((w) => w.name.toLowerCase() === workflowName.toLowerCase());
      let msg = `Workflow named "${workflowName}" not found.`;
      if (candidateWorkflow) {
        msg = `Workflow named "${workflowName}" not found. Did you mean: "${candidateWorkflow.name}"?`;
      }

      throw new Error(msg);
    }

    return workflow;
  }

  private findWorkflowByCode(code: string) {
    if (!code) {
      throw new Error('Parameter "code" required, but is null.');
    }

    let workflow = this._workflows.find((w) => w.code === code);
    return workflow;
  }

  /**
   * Sets all step's isComplete properties to false
   * @param workflowCode
   */
  public resetWorkflow(workflowCode: string) {
    const workflow = this.findWorkflowByCode(workflowCode);

    if (!workflow) {
      return;
    }

    workflow.steps.forEach((step) => {
      step.isComplete = false;
    });
  }

  /**
   * Gets the current step of the workflow, defined as the first incomplete step
   */
  public getCurrentStep(workflowName: string): WorkflowStep {
    const steps = this.getIncompleteSteps(workflowName);

    if (!steps) {
      return null;
    }

    if (steps.length == 0) {
      return null;
    } else {
      return steps[0];
    }
  }

  /**
   * Gets all of the incomplete steps from a workflow
   */
  public getIncompleteSteps(workflowName: string) {
    const workflow = this.findWorkflow(workflowName);

    return workflow.steps.filter((x) => x.isComplete === false);
  }

  /**
   * Gets all reasons
   */
  public getReasons() {
    return this.http.get<Reason[]>('workflow/reason');
  }

  /**
   * Gets a workflow by Workflow.code
   * @param code
   */
  public getWorkflow(code: string): Workflow {
    return this._workflows.find((x) => this.valueService.equalsIgnoringCase(x.code, code));
  }

  /**
   * Iterates over a workflow display node and tests the step names for complete status in a workflow
   * @param node 
   * @param workflow 
   * @returns 
   */
  public isNodeComplete(node: WorkflowDisplayNodeViewModel, workflow: Workflow): boolean {
    let b = true;
    
    node.stepNames.forEach(s=> {
      if (!workflow.steps.find(x=> x.name === s).isComplete){
        b = false;
      }
    });

    return b;
  }

  /**
   * Is the route name the last step in the workflow? 
   * @param workflow 
   * @param route 
   */
  public isLastStep(workflow: Workflow, route: string) {
    if (workflow && workflow.steps && workflow.steps.length > 0){
      return workflow.steps[workflow.steps.length -1].route === route;
    }

    return false;
  }
}
